import { EAGLE_DARK, EAGLE_WHITE } from "./images";

// Photohawk is two brands, and the site names them the same way everywhere (header, page heroes, the home page,
// the footer, login and register; owner decision, 21 October 2026):
//   Photohawk:          the phone app for the photos of your people (people, sharing, chats, memories, prints). Its
//                       mark is "Photohawk". In copy (marketing, help centre, blog; owner, 29 Oct 2026) it is
//                       "Friends & Family", as on the App Store ("Photohawk: Friends & Family"): the first mention on a
//                       page may be "Photohawk Friends & Family", then "Friends & Family". Never "the app", "the
//                       Photohawk app", "Photohawk Family" or "Photohawk Personal" (Personal and Family are its PLANS).
//                       The legal texts (src/content/personalLegal, the face data policy, terms, privacy) keep their own
//                       wording until legal review.
//   Photohawk Studio:   for photographers and event businesses (galleries, selling, Find your photos, live upload,
//                       the Studio app and the full admin).
// The two stay apart: nothing in a Studio gallery, email or download links to or promotes the app (the only Photohawk
// mark there is Lite's "Powered by Photohawk" badge). Product names are names: they aren't translated (like the plan
// names Lite, Pro and Max).
//
// Each product has its own icon and badge: the app a phone in an outlined pill ("App"), Studio a camera in a filled
// amber pill. Both use the logo's amber, so the badge's shape and icon, not a second brand colour, tell them apart.

export type Product = "personal" | "studio";

export const PRODUCT_NAME: Record<Product, string> = {
  personal: "Photohawk",
  studio: "Photohawk Studio",
};
/** The short name, on the product badges ("App", "Studio"). */
export const PRODUCT_SHORT: Record<Product, string> = { personal: "Friends & Family", studio: "Studio" };
/** The word after "Photohawk" in a product lock-up: none for the app ("Photohawk"), "Studio" for Studio. */
export const PRODUCT_WORDMARK: Record<Product, string> = { personal: "", studio: "Studio" };

export const PhoneIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
    <path d="M10.5 18.5h3" />
  </svg>
);
export const CameraIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8h3l1.6-2.4A1.5 1.5 0 0 1 9.9 5h4.2a1.5 1.5 0 0 1 1.3.6L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);

export const SearchIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </svg>
);

/** Two people: Friends & Family's mark (owner, 3 Nov 2026: named by who it's for, not "App", as Studio is an app too). */
export const PeopleIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M16 14.2c3 .1 5.5 2.1 5.5 5.3" />
  </svg>
);

export const ProductIcon = ({ product, size = 18 }: { product: Product; size?: number }) =>
  product === "personal" ? <PeopleIcon size={size} /> : <CameraIcon size={size} />;

/**
 * The product's badge: "Friends & Family" (two people, outlined) or "Studio" (a camera, amber). `tone` is the ground it sits
 * on: light (the site) or dark (a photo). Every text pair is AA: ink on amber; ink on the light grounds; white on
 * the dark ones. Plain hex, not the site's tokens, as it's also on /login and /register.
 */
export const ProductBadge = ({ product, tone = "light", className = "" }: { product: Product; tone?: "dark" | "light"; className?: string }) => {
  const look =
    product === "studio"
      ? "bg-[#EA8A23] text-[#121721] border-[#EA8A23]"
      : tone === "dark"
        ? "border-white text-white"
        : "border-[#121721] text-[#121721]";
  return (
    <span
      className={`inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-full border-[1.5px] px-2.5 text-[13px] font-bold leading-none ${look} ${className}`}
      data-product-badge={product}
    >
      <ProductIcon product={product} size={15} />
      {PRODUCT_SHORT[product]}
    </span>
  );
};

/**
 * The product lock-up: the eagle, "Photohawk" and the product's name, as one mark ("Photohawk Studio"). Read out as
 * the product's full name.
 */
export const ProductLockup = ({ product, tone = "light", size = "md" }: { product: Product; tone?: "dark" | "light"; size?: "md" | "lg" }) => {
  const big = size === "lg";
  return (
    <span className={`inline-flex items-center gap-2 ${tone === "dark" ? "text-white" : "text-foreground"}`} data-product-lockup={product}>
      <img
        src={tone === "dark" ? EAGLE_WHITE : EAGLE_DARK}
        alt=""
        width={big ? 30 : 24}
        height={big ? 32 : 25}
        className={`${big ? "h-8 w-[30px]" : "h-[25px] w-6"} object-contain`}
      />
      <span className={`font-display font-bold tracking-[-0.02em] ${big ? "text-[22px]" : "text-[17px]"}`}>
        Photohawk
        {PRODUCT_WORDMARK[product] && (
          <>
            {" "}
            <span className={tone === "dark" ? "text-[#EA8A23]" : "text-[#A6560A]"}>{PRODUCT_WORDMARK[product]}</span>
          </>
        )}
      </span>
    </span>
  );
};

/** A product page's hero line: the lock-up, then who it's for. */
export const ProductEyebrow = ({ product, text, tone = "light" }: { product: Product; text: string; tone?: "dark" | "light" }) => (
  <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-2 self-start" data-testid={`${product}-eyebrow`}>
    <ProductBadge product={product} tone={tone} />
    <span className={`text-[13px] font-semibold uppercase tracking-[0.1em] ${tone === "dark" ? "text-white/80" : "text-muted-foreground"}`}>{text}</span>
  </span>
);
